import React, { ReactNode } from "react";
import Modal from "../Modal";
import { EButtonVariant, EButtonstyletype } from "../Button";
import { ConfirmModalContent } from "./ConfirmModal/ConfirmModalContent";
import { useConfirmModalState } from "./ConfirmModal/useConfirmModalState";

/**
 * Props for ConfirmModal component
 */
export type ConfirmModalProps = {
	// État
	isOpen: boolean;
	onClose: () => void;

	// Contenu
	title: string;
	message?: string | ReactNode; // Message principal
	children?: ReactNode; // Contenu additionnel (remplace message si fourni)

	// Variante
	variant?: "danger" | "warning" | "info"; // Par défaut: "info"

	// Boutons
	confirmButton?: {
		label: string;
		onClick: () => void | Promise<void>;
		variant?: EButtonVariant; // Par défaut: selon variant
		styleType?: EButtonstyletype; // Par défaut: EButtonstyletype.CONTAINED
		disabled?: boolean;
		loading?: boolean;
		fullwidth?: boolean;
		leftIcon?: ReactNode;
		rightIcon?: ReactNode;
	};

	cancelButton?: {
		label: string; // Par défaut: "Annuler"
		onClick?: () => void; // Par défaut: onClose
		variant?: EButtonVariant; // Par défaut: EButtonVariant.SECONDARY
		styleType?: EButtonstyletype; // Par défaut: EButtonstyletype.TEXT
		fullwidth?: boolean;
	};

	// Confirmation par saisie (optionnel)
	requireConfirmation?: {
		enabled: boolean;
		label: string; // Label du champ de confirmation
		placeholder: string; // Placeholder
		expectedValue: string; // Valeur attendue
		errorMessage?: string; // Message d'erreur si valeur incorrecte
	};

	// Actions additionnelles
	thirdButton?: {
		label: string;
		onClick: () => void;
		variant?: EButtonVariant;
		styleType?: EButtonstyletype;
		fullwidth?: boolean;
	};

	// Callbacks
	onConfirm?: () => void | Promise<void>; // Alias pour confirmButton.onClick
	onCancel?: () => void; // Alias pour cancelButton.onClick

	// États
	loading?: boolean; // État de chargement global
	error?: string | null; // Message d'erreur à afficher

	// Styles
	className?: string;
	fullwidth?: boolean;
	fullscreen?: boolean;
	fullheight?: boolean;

	// Comportement
	closeOnBackdropClick?: boolean; // Par défaut: true
	closeOnEscape?: boolean; // Par défaut: true
};

type ConfirmModalState = ReturnType<typeof useConfirmModalState>;

function renderConfirmModalInner(props: ConfirmModalProps, state: ConfirmModalState): React.JSX.Element {
	const { isOpen, title, message, children, error, className, fullwidth, fullscreen, fullheight, closeOnBackdropClick } = props;
	const { handleClose, confirmButtonProps, cancelButtonProps, thirdButtonProps, confirmationValue, onConfirmationChange } = state;
	return (
		<Modal
			isOpen={isOpen}
			onClose={closeOnBackdropClick ? handleClose : undefined}
			title={title}
			className={className}
			fullwidth={fullwidth}
			fullscreen={fullscreen}
			fullheight={fullheight}
			firstButton={cancelButtonProps}
			secondButton={confirmButtonProps}
			thirdButton={thirdButtonProps}>
			<ConfirmModalContent
				message={message}
				content={children}
				requireConfirmation={props.requireConfirmation}
				confirmationValue={confirmationValue}
				onConfirmationChange={onConfirmationChange}
				error={error}
			/>
		</Modal>
	);
}

/**
 * Composant générique pour les modals de confirmation avec :
 * - Titre et message configurables
 * - Boutons d'action configurables
 * - Variantes (danger, warning, info)
 * - Support de confirmation par saisie
 */
export default function ConfirmModal(props: ConfirmModalProps): React.JSX.Element {
	const state = useConfirmModalState({
		isOpen: props.isOpen,
		onClose: props.onClose,
		variant: props.variant ?? "info",
		confirmButton: props.confirmButton,
		cancelButton: props.cancelButton,
		requireConfirmation: props.requireConfirmation,
		thirdButton: props.thirdButton,
		onConfirm: props.onConfirm,
		onCancel: props.onCancel,
		loading: props.loading ?? false,
		closeOnBackdropClick: props.closeOnBackdropClick ?? true,
	});
	return renderConfirmModalInner(props, state);
}
